<template>
  <div class="form-page form-application">
    <div class="full-frame-container">
      <div class="container">

        <div class="form-box">
          <!-- Left: Logo -->
          <div class="column-left">
            <b-img class="d-block logo" src="../assets/logo_text.png"></b-img>
          </div>

          <!-- Right: Form -->
          <div class="column-right">
            <div class="form-container">

              <!-- Step: Request Reset Link -->
              <template v-if="step === 'request'">
                <div class="header-description">
                  <h3 class="mb-2">FORGOT PASSWORD?</h3>
                  <h4 class="mb-4">Enter your registered email and we'll send you a reset link.</h4>
                </div>

                <input
                  v-model="email"
                  type="email"
                  placeholder="Email address"
                  class="form-control"
                  :class="{ 'input-error': emailTouched && email && !isValidEmail(email) }"
                  @input="onEmailInput"
                  @keyup.enter="submitRequest"
                />
                <div v-if="emailTouched && email && !isValidEmail(email)" class="error-msg mb-2">
                  Invalid email address.
                </div>
                <div v-if="requestError" class="error-msg mt-2 text-center">{{ requestError }}</div>

                <div class="d-flex justify-content-center mt-5">
                  <button
                    class="btn btn-submit px-5 py-2"
                    :disabled="!email || !isValidEmail(email) || loading"
                    @click="submitRequest"
                  >
                    <fa-icon v-if="loading" :icon="['fas', 'spinner']" :spin="true" class="mr-2" />
                    Send Reset Link
                  </button>
                </div>

                <div class="mt-4 text-center">
                  <span class="back-link" @click="goToLogin">← Back to Login</span>
                </div>
              </template>

              <!-- Step: Email Sent Confirmation -->
              <template v-if="step === 'sent'">
                <div class="status-state">
                  <div class="status-icon success">
                    <fa-icon :icon="['fas', 'check-circle']" />
                  </div>
                  <h3 class="mb-2">Check Your Email</h3>
                  <p class="status-desc mb-5">
                    Reset instructions have been sent to your inbox.
                    The link will expire in <strong>15 minutes</strong>.
                  </p>
                  <button class="btn btn-submit px-5 py-2" @click="goToLogin">
                    Back to Login
                  </button>
                </div>
              </template>

              <!-- Step: Set New Password (token in URL) -->
              <template v-if="step === 'reset'">
                <div class="header-description">
                  <h3 class="mb-2">SET NEW PASSWORD</h3>
                  <h4 class="mb-4">Enter and confirm your new password below.</h4>
                </div>

                <div class="password-wrapper">
                  <input
                    v-model="newPassword"
                    :type="showPassword ? 'text' : 'password'"
                    placeholder="New password (min. 8 characters)"
                    class="form-control"
                    :class="{ 'input-error': passwordTouched && newPassword && newPassword.length < 8 }"
                    @input="passwordTouched = true; resetError = ''"
                  />
                  <button type="button" class="toggle-password" @click="showPassword = !showPassword">
                    <fa-icon :icon="showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']" />
                  </button>
                </div>
                <div v-if="passwordTouched && newPassword && newPassword.length < 8" class="error-msg mb-2">
                  Password must be at least 8 characters.
                </div>

                <div class="password-wrapper mt-3">
                  <input
                    v-model="confirmPassword"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    placeholder="Confirm new password"
                    class="form-control"
                    :class="{ 'input-error': confirmTouched && confirmPassword && newPassword !== confirmPassword }"
                    @input="confirmTouched = true; resetError = ''"
                  />
                  <button type="button" class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">
                    <fa-icon :icon="showConfirmPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']" />
                  </button>
                </div>
                <div v-if="confirmTouched && confirmPassword && newPassword !== confirmPassword" class="error-msg mb-2">
                  Passwords do not match.
                </div>

                <div v-if="resetError" class="error-msg mt-2 text-center">{{ resetError }}</div>

                <div class="d-flex justify-content-center mt-5">
                  <button
                    class="btn btn-submit px-5 py-2"
                    :disabled="!isResetValid || loading"
                    @click="submitReset"
                  >
                    <fa-icon v-if="loading" :icon="['fas', 'spinner']" :spin="true" class="mr-2" />
                    Reset Password
                  </button>
                </div>
              </template>

              <!-- Step: Reset Success -->
              <template v-if="step === 'success'">
                <div class="status-state">
                  <div class="status-icon success">
                    <fa-icon :icon="['fas', 'check-circle']" />
                  </div>
                  <h3 class="mb-2">Password Reset!</h3>
                  <p class="status-desc mb-5">
                    Your password has been updated successfully. You can now login with your new password.
                  </p>
                  <button class="btn btn-submit px-5 py-2" @click="goToLogin">
                    Back to Login
                  </button>
                </div>
              </template>

              <!-- Step: Token Expired / Invalid -->
              <template v-if="step === 'expired'">
                <div class="status-state">
                  <div class="status-icon warning">
                    <fa-icon :icon="['fas', 'exclamation-circle']" />
                  </div>
                  <h3 class="mb-2">Link Expired</h3>
                  <p class="status-desc mb-5">
                    This password reset link is invalid or has expired. Please request a new one.
                  </p>
                  <button class="btn btn-submit px-5 py-2" @click="restartRequest">
                    Request New Link
                  </button>
                  <div class="mt-3">
                    <span class="back-link" @click="goToLogin">← Back to Login</span>
                  </div>
                </div>
              </template>

            </div>

            <div class="mt-2 text-center">
              <p class="font-weight-normal text-sm">
                Remembered your password?
                <span class="text-goldenrod cursor-pointer" @click="goToLogin">Login here</span>
              </p>
            </div>
          </div>
        </div>

        <div class="copyright text-xs font-weight-light">Copyright © 2020 TYNG & CO (NF 2148)</div>
      </div>

      <!-- Email Not Found Modal -->
      <div v-if="showEmailNotFoundModal" class="modal-overlay">
        <div class="modal-content enhanced-modal">
          <div class="modal-icon-wrap">
            <fa-icon :icon="['fas', 'exclamation-circle']" class="modal-warn-icon" />
          </div>
          <h5 class="modal-title mb-3">Email Not Found</h5>
          <p class="modal-description mb-4">
            No account was found with the email address<br>
            <strong>{{ email }}</strong>
          </p>
          <p class="modal-description mb-5">
            Please check your spelling or contact us if you need help accessing your account.
          </p>
          <div class="d-flex justify-content-center" style="gap: 10px;">
            <button class="btn btn-secondary px-4 py-1" @click="showEmailNotFoundModal = false">
              Try Again
            </button>
            <router-link :to="`/${$i18n.locale}/contact-us?type=2`" class="btn btn-submit px-4 py-1">
              Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { lockBodyScroll, unlockBodyScroll } from '@/utils/bodyScrollLock'

export default {
  name: 'ResetPassword',
  metaInfo () {
    return {
      title: 'Tyng & Co | Reset Password',
      titleTemplate: null
    }
  },
  data () {
    return {
      step: 'request',
      token: '',
      // Request step
      email: '',
      emailTouched: false,
      requestError: '',
      loading: false,
      showEmailNotFoundModal: false,
      // Reset step
      newPassword: '',
      confirmPassword: '',
      passwordTouched: false,
      confirmTouched: false,
      showPassword: false,
      showConfirmPassword: false,
      resetError: ''
    }
  },
  computed: {
    isResetValid () {
      return this.newPassword.length >= 8 && this.newPassword === this.confirmPassword
    }
  },
  watch: {
    showEmailNotFoundModal (val) {
      val ? lockBodyScroll() : unlockBodyScroll()
    }
  },
  beforeDestroy () {
    if (this.showEmailNotFoundModal) unlockBodyScroll()
  },
  mounted () {
    const token = this.$route.query.token
    if (token) {
      this.token = token
      this.step = 'reset'
    }
  },
  methods: {
    goToLogin () {
      this.$router.push(`/${this.$i18n.locale}/login`)
    },

    restartRequest () {
      this.token = ''
      this.email = ''
      this.emailTouched = false
      this.requestError = ''
      this.step = 'request'
      this.$router.replace({ query: {} })
    },

    isValidEmail (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },

    onEmailInput () {
      this.emailTouched = true
      this.requestError = ''
    },

    async submitRequest () {
      if (!this.isValidEmail(this.email)) {
        this.emailTouched = true
        return
      }

      this.loading = true
      this.requestError = ''

      try {
        const res = await fetch('https://tyng-ca.com.my/api/client/password/forgot.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: this.email.trim() })
        })

        const data = await res.json()

        if (data.success) {
          this.step = 'sent'
        } else if (data.error === 'email_not_found') {
          this.showEmailNotFoundModal = true
        } else {
          this.requestError = data.message || 'Unable to process your request. Please try again.'
        }
      } catch (err) {
        console.error('Forgot password error:', err)
        this.requestError = 'Server error. Please try again later.'
      } finally {
        this.loading = false
      }
    },

    async submitReset () {
      if (!this.isResetValid) return

      this.loading = true
      this.resetError = ''

      try {
        const res = await fetch('https://tyng-ca.com.my/api/client/password/reset.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: this.token,
            password: this.newPassword
          })
        })

        const data = await res.json()

        if (data.success) {
          this.step = 'success'
        } else if (res.status === 401) {
          this.step = 'expired'
        } else {
          this.resetError = data.message || 'Unable to reset password. Please try again.'
        }
      } catch (err) {
        console.error('Reset password error:', err)
        this.resetError = 'Server error. Please try again later.'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
